import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { statesLabel } from "@/lib/pricing";
import { Database, Mail, Phone, MapPinned, TrendingUp } from "lucide-react";

// Final values render immediately (no count-up): visitors must never see a low interim number.
function Stat({ icon: Icon, label, value, sub, tone, testid, format = (n) => n.toLocaleString() }) {
  return (
    <div className="flex-1 min-w-[150px] border border-border/60 bg-black/40 backdrop-blur-sm px-5 py-4" data-testid={testid}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <Icon className={`w-3.5 h-3.5 ${tone}`} /> {label}
      </div>
      <div className={`mt-1 font-heading font-black text-3xl sm:text-4xl tabular-nums ${tone}`} data-testid={`${testid}-value`}>
        {value == null ? "—" : format(value)}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-white/60">{sub}</div>}
    </div>
  );
}

function Sparkline({ series, testid }) {
  if (!series || series.length < 2) return null;
  const W = 640, H = 90, P = 6;
  const vals = series.map((p) => p.total);
  const golds = series.map((p) => p.gold);
  const min = Math.min(...vals, ...golds), max = Math.max(...vals);
  const x = (i) => P + (i / (series.length - 1)) * (W - 2 * P);
  const y = (v) => H - P - ((v - min) / Math.max(1, max - min)) * (H - 2 * P);
  const path = (arr) => arr.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const first = series[0], last = series[series.length - 1];
  const delta = last.total - first.total;
  return (
    <div className="mt-5 border border-border/60 bg-black/40 px-5 py-4" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <span>Last 30 days · database growth</span>
        <span><span className="text-white">— total records</span> · <span className="text-neon-yellow">— gold</span> · <span className="text-emerald-300">+{delta.toLocaleString()} in 30 days</span></span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24 mt-2" preserveAspectRatio="none" role="img" aria-label="30-day database growth">
        <defs>
          <linearGradient id="ldsFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${path(vals)} L${x(series.length - 1).toFixed(1)},${H - P} L${x(0).toFixed(1)},${H - P} Z`} fill="url(#ldsFill)" />
        <path d={path(vals)} fill="none" stroke="#ffffff" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        <path d={path(golds)} fill="none" stroke="#f5d90a" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeDasharray="4 3" />
        <circle cx={x(series.length - 1)} cy={y(last.total)} r="4" fill="#ffffff" />
      </svg>
      <div className="flex justify-between font-mono text-[10px] text-muted-foreground">
        <span>{first.day} · {first.total.toLocaleString()}</span>
        <span>{last.day} · {last.total.toLocaleString()}</span>
      </div>
    </div>
  );
}

export default function LiveDataStrip({ compact = false }) {
  const [s, setS] = useState(() => globalThis.__AO_STATS__ || null);
  useEffect(() => {
    api.get("/public/data-stats").then((r) => setS(r.data)).catch(() => {});
  }, []);
  const growth = s?.added_7d ? `+${s.added_7d.toLocaleString()} added in the last 7 days` : s?.added_30d ? `+${s.added_30d.toLocaleString()} added in the last 30 days` : "New verified data added every day";
  return (
    <section className="relative border-y border-neon-yellow/25 bg-gradient-to-r from-black via-neon-yellow/[0.04] to-black" data-testid="live-data-strip">
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 ${compact ? "py-6" : "py-8 sm:py-10"}`}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-neon-yellow flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 bg-neon-yellow rounded-full animate-pulse" aria-hidden /> The database · live count
            </div>
            <h2 className="mt-1 font-heading font-black text-base md:text-lg text-white">
              Verified businesses you can call and email today — <span className="text-neon-yellow">and it grows every day.</span>
            </h2>
          </div>
          <div className="inline-flex items-center gap-2 border border-emerald-400/50 text-emerald-300 font-mono text-[10px] uppercase tracking-widest px-3 py-1.5" data-testid="live-data-growth">
            <TrendingUp className="w-3.5 h-3.5" /> {growth}
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <Stat icon={Database} label="Total database" value={s?.total} sub="Every business record we hold — retail, distributors, jobbers, landlords" tone="text-white" testid="live-data-total" />
          <Stat icon={Database} label="Verified & live" value={s?.businesses} sub="Published venues you can search today" tone="text-emerald-300" testid="live-data-businesses" />
          <Stat icon={Mail} label="Gold · owner email verified" value={s?.gold} sub="Decision-maker email, verified deliverable" tone="text-neon-yellow" testid="live-data-gold" />
          <Stat icon={Phone} label="Direct phone lines" value={s?.phones} sub="Call-ready business numbers" tone="text-neon-cyan" testid="live-data-phones" />
          <Stat icon={MapPinned} label="States covered" value={s?.states} format={(n) => statesLabel(n) || "—"} sub="50 states + DC, one platform" tone="text-neon-magenta" testid="live-data-states" />
        </div>
        {!compact && <Sparkline series={s?.series} testid="live-data-growth-chart" />}
        {!compact && s?.by_type?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="live-data-by-type">
            {s.by_type.map((t) => (
              <span key={t.business_type}><span className="text-white/80">{t.count.toLocaleString()}</span> {t.business_type}</span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
